import useLocalization from "@/hooks/useLocalization";
import { DashboardMetricTotals } from "../types";
import {
  formatInteger,
  formatRevenue,
  getAcceptanceRate,
  getComparison,
  getGuestsPerReservation,
} from "../utils";
import DashboardGrid from "./DashboardGrid";
import MetricCard from "./MetricCard";

type MetricsSummaryGridProps = {
  rollingWindowDays: number;
  rollingMetrics: {
    current: DashboardMetricTotals;
    previous: DashboardMetricTotals;
  };
};

const isNewMetric = (currentValue: number, previousValue: number) =>
  previousValue === 0 && currentValue > 0;

export default function MetricsSummaryGrid({
  rollingWindowDays,
  rollingMetrics,
}: MetricsSummaryGridProps) {
  const { t } = useLocalization();
  const rollingGuests = getComparison(
    rollingMetrics.current.guests,
    rollingMetrics.previous.guests
  );
  const rollingReservations = getComparison(
    rollingMetrics.current.reservations,
    rollingMetrics.previous.reservations
  );
  const rollingRevenue = getComparison(
    rollingMetrics.current.revenue,
    rollingMetrics.previous.revenue
  );
  const rollingAcceptanceRate = getAcceptanceRate(rollingMetrics.current);
  const previousRollingAcceptanceRate = getAcceptanceRate(
    rollingMetrics.previous
  );
  const roundedRollingAcceptanceRate = Math.round(rollingAcceptanceRate);
  const roundedPreviousRollingAcceptanceRate = Math.round(
    previousRollingAcceptanceRate
  );
  const rollingAcceptanceDelta =
    roundedRollingAcceptanceRate - roundedPreviousRollingAcceptanceRate;

  return (
    <DashboardGrid>
      <MetricCard
        label={t("dashboard:reservationsLastDays", {
          interpolation: { count: rollingWindowDays },
        })}
        value={rollingReservations.value}
        previousValue={rollingReservations.previousValue}
        deltaPercent={rollingReservations.deltaPercent}
        deltaLabel={
          isNewMetric(
            rollingReservations.value,
            rollingReservations.previousValue
          )
            ? t("dashboard:newMetric")
            : undefined
        }
        detail={t("dashboard:guestsPerReservation", {
          interpolation: {
            count: getGuestsPerReservation(rollingMetrics.current),
          },
        })}
        previousValueLabel={t("dashboard:previousDays", {
          interpolation: { count: rollingWindowDays },
        })}
      />

      <MetricCard
        label={t("dashboard:acceptanceLastDays", {
          interpolation: { count: rollingWindowDays },
        })}
        value={roundedRollingAcceptanceRate}
        formatValue={(value) =>
          t("dashboard:acceptedPercent", {
            interpolation: { percent: value },
          })
        }
        previousValue={`${roundedPreviousRollingAcceptanceRate}%`}
        deltaPercent={rollingAcceptanceDelta}
        detail={`${t("dashboard:acceptedRejectedDetail", {
          interpolation: {
            accepted: formatInteger(rollingMetrics.current.accepted),
            rejected: formatInteger(rollingMetrics.current.rejected),
          },
        })}, ${t("dashboard:pendingRequests", {
          interpolation: { count: rollingMetrics.current.pending },
        })}, ${t("dashboard:unansweredRequests", {
          interpolation: { count: rollingMetrics.current.unanswered },
        })}`}
        previousValueLabel={t("dashboard:previousDays", {
          interpolation: { count: rollingWindowDays },
        })}
      />

      <MetricCard
        label={t("dashboard:guestsLastDays", {
          interpolation: { count: rollingWindowDays },
        })}
        value={rollingGuests.value}
        previousValue={rollingGuests.previousValue}
        deltaPercent={rollingGuests.deltaPercent}
        deltaLabel={
          isNewMetric(rollingGuests.value, rollingGuests.previousValue)
            ? t("dashboard:newMetric")
            : undefined
        }
        previousValueLabel={t("dashboard:previousDays", {
          interpolation: { count: rollingWindowDays },
        })}
      />

      <MetricCard
        label={t("dashboard:revenueLastDays", {
          interpolation: { count: rollingWindowDays },
        })}
        value={rollingRevenue.value}
        formatValue={formatRevenue}
        previousValue={formatRevenue(rollingRevenue.previousValue)}
        deltaPercent={rollingRevenue.deltaPercent}
        deltaLabel={
          isNewMetric(rollingRevenue.value, rollingRevenue.previousValue)
            ? t("dashboard:newMetric")
            : undefined
        }
        previousValueLabel={t("dashboard:previousDays", {
          interpolation: { count: rollingWindowDays },
        })}
      />
    </DashboardGrid>
  );
}
